import { ComponentInjector, ComponentSelector, } from '@atypon/ui-utils';
import CoreAdvancedSearch, {
    AdvancedSearchElements,
    AdvancedSearchSelectors,
    AdvancedSearchClassList,
    AdvancedSearchTexts
} from 'ui-core/widgets/advanced-search/js/advanced-search';


class Texts extends AdvancedSearchTexts { }

class ClassList extends AdvancedSearchClassList { }

class Selectors extends AdvancedSearchSelectors {
    staticRangeLabel = new ComponentSelector('[for="staticRange"]');
    staticRangeList = new ComponentSelector('#staticRangeSelect');
    refineSearchBtn = new ComponentSelector('.advanced-search__ctrl', '.search__result');
    tabLinks = new ComponentSelector('.tab__nav__item__link', '.advanced-search__tabs');
    tabLinksWrappers = new ComponentSelector('.tab__nav__item', '.advanced-search__tabs');
    citationSearchTab = new ComponentSelector('.citation-search__tab a', '.advanced-search__top-section');
    advancedSearchTab = new ComponentSelector('.adv-search__tab a', '.advanced-search__top-section');
    advancedSearchTitle = new ComponentSelector('.advanced-search__title', '.advanced-search__top-section');
    publishedInInput = new ComponentSelector('.advanced-search__autocomplete' );
    autoCompleteTag = 'autoComplete__tag';
    selectedInput = new ComponentSelector('#anyDate');
}

class Elements extends AdvancedSearchElements {
    staticRangeLabel: HTMLElement;
    staticRangeList: HTMLElement;
    refineSearchBtn: HTMLElement;
    tabLinks: Array<HTMLElement> = [];
    tabLinksWrappers: Array<HTMLElement> = [];
    citationSearchTab: HTMLElement;
    advancedSearchTab: HTMLElement;
    advancedSearchTitle: HTMLElement;
    publishedInInput: HTMLElement;
    selectedInput: HTMLElement;
}

interface AdvancedSearch {
    get elements(): Elements;
    get selectors(): Selectors;
    get classList(): ClassList;
    get texts(): Texts;
}

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class AdvancedSearch extends CoreAdvancedSearch {
    initialize() {
        super.initialize();
        this.eventListeners();
        this.additionalControls();
    }

    eventListeners() {
        this.elements.staticRangeList?.addEventListener('change', this.changeStaticRangeAriaLabel.bind(this))
        this.elements.refineSearchBtn?.addEventListener('click', this.toggleSearchBlock.bind(this))
        this.elements.tabLinks?.forEach(tabLink => {
            tabLink.addEventListener('focus', this.tabLinksFocusHandler.bind(this))
        });
        this.elements.advancedSearchTab?.addEventListener('focus', this.TopSectionsOnAdvancedSearchFocusHandler.bind(this))
        this.elements.citationSearchTab?.addEventListener('focus', this.TopSectionsOnCitationFocusHandler.bind(this))
        window.addEventListener("load", this.TopSectionsOnLoadHandler.bind(this))
        this.domUtils.addEventListener(this.elements.publishedInInput , "focus , focusout" , this.publishedInFocusHandler.bind(this))

    }
    additionalControls() {
        this.changeStaticRangeAriaLabel();
        this.A11yHandler();
        this.publicationDateA11yHandler();
    }

    /**
     * To handle the A11Y for Voice Over
     */
    A11yHandler() {
        this.elements.advancedSearchTitle && (this.elements.advancedSearchTitle.tabIndex = -1);

    }

    /**
     * Enable the submit button only if the search term field isn't empty
     */
    setSubmitButtonState(event?: CustomEvent) {
        const state = this.hasEmptyFields();
        if (state) {
            this.elements.submitButton.setAttribute('disabled', `${state}`);
        } else {
            this.elements.submitButton.removeAttribute('disabled');
        }
    }

    /**
     * To handle the A11Y for the static range field
     */
    changeStaticRangeAriaLabel() {
        let selectedOption = this.elements.staticRangeList.querySelector('option:checked');
        this.elements.staticRangeLabel.setAttribute('aria-label', 'Last ' + selectedOption.textContent);
    }

    /**
     * Slide-in & out for refine search
     */
    toggleSearchBlock(e) {
        e.preventDefault();
        const thisEl = e.currentTarget,
            targetContent = this.domUtils.getElement('.advanced-search__tabs'),
            advancedSearchTabsNav = this.domUtils.getElement('.advancedSearch__tabs__nav', targetContent),
            tabNavCurrentItem = this.domUtils.getElement('.tab__nav__item.js--active', advancedSearchTabsNav),
            tabNavFirstItem = this.domUtils.getElement('.tab__nav__item', advancedSearchTabsNav);

        thisEl.setAttribute('aria-expanded', thisEl.getAttribute('aria-expanded') === 'false' ? 'true' : 'false');
        this.domUtils.animate('slideToggle', targetContent);
        if (thisEl.classList.contains('js--open')) {
            let focusTarget = tabNavCurrentItem;
            if (!focusTarget) {
                focusTarget = tabNavFirstItem;
            }
            let itemLink = this.domUtils.getElement('.tab__nav__item__link', focusTarget);
            itemLink.focus();
            itemLink.setAttribute('aria-selected', 'true');
            itemLink.setAttribute('tabindex', '0');
        }

    }

    /**
     * To handle the A11Y on the tabs in refine search
     */
    tabLinksFocusHandler(e) {
        const parent = e.target.parentElement;
        this.elements.tabLinksWrappers.forEach(wrapper => wrapper.classList.remove('js--active'));
        parent.classList.add('js--active');
        this.elements.tabLinks.forEach(link => {
            link.setAttribute('aria-selected', 'false');
            link.setAttribute('tabindex', '-1');
        });
        e.target.setAttribute('aria-selected', 'true');
        e.target.setAttribute('tabindex', '0');
    };

    changeHashForCitationSearch() {
        window.location.hash = '#citation';
        (window as any).adobeDataLayer && (window as any).UX.adobeDigitalData.changeTabsLoad(this.elements.citationSearchTab);
    }
    changeHashForAdvancedSearch() {
        window.location.hash = '';
        (window as any).adobeDataLayer && (window as any).UX.adobeDigitalData.changeTabsLoad(this.elements.advancedSearchTab);
    }

    TopSectionsOnLoadHandler() {
        if (window.location.hash === '#citation') {
            this.TopSectionsOnCitationFocusHandler()
            this.elements.citationSearchTab.click()
        }
    }
    /**
     * To handle the A11Y on the tabs in advancedSearch & citationSearch Tabs
     */
    TopSectionsOnCitationFocusHandler() {
        this.changeHashForCitationSearch()
        this.elements.citationSearchTab?.setAttribute('tabindex', '0');
        this.elements.citationSearchTab?.setAttribute('aria-selected', 'true');
        this.elements.advancedSearchTab?.setAttribute('tabindex', '-1');
        this.elements.advancedSearchTab?.setAttribute('aria-selected', 'false');
        this.domUtils.getElement(this.selectors.citationSearchTab.selector)?.parentElement.classList.add('js--active');
        this.domUtils.getElement(this.selectors.advancedSearchTab.selector)?.parentElement.classList.remove('js--active');

    }

    TopSectionsOnAdvancedSearchFocusHandler() {
        this.changeHashForAdvancedSearch()
        this.elements.citationSearchTab?.setAttribute('tabindex', '-1');
        this.elements.citationSearchTab?.setAttribute('aria-selected', 'false');
        this.elements.advancedSearchTab?.setAttribute('tabindex', '0');
        this.elements.advancedSearchTab?.setAttribute('aria-selected', 'true');
        this.domUtils.getElement(this.selectors.advancedSearchTab.selector)?.parentElement.classList.add('js--active');
        this.domUtils.getElement(this.selectors.citationSearchTab.selector)?.parentElement.classList.remove('js--active');
    }

    publicationDateA11yHandler() {
        const elements = this.domUtils.getElements(
            '#publication ,#anyDate, #staticRange, #staticRangeSelect, #customRange, #fromMonth, #fromYear, #toMonth, #toYear'
        );
        elements.forEach((element: HTMLInputElement, index) => {
            element.addEventListener('focus', () => {
                if (element.type === 'radio') {
                    element.click();
                    this.elements.selectedInput = element;
                }
            });

            element.addEventListener('keydown', event => {
                if (event.key === 'Tab' && !event.shiftKey) {
                    if (index === elements.length - 1 || element.getAttribute('id') === 'staticRangeSelect' || element.getAttribute('id') === 'anyDate') {
                        return;
                    } else {
                        if (element.getAttribute('id') === 'publication') {
                            setTimeout(() => {
                                this.elements.selectedInput.focus();
                            }, 0);
                        } else {
                            event.preventDefault();
                            const nextIndex = index + 1;
                            elements[nextIndex].focus();
                        }
                    }
                } else if (event.shiftKey && event.key === 'Tab') {
                    if (index === 0 || element.type === 'radio') {
                        return;
                    } else {
                        if (element.getAttribute('id') === 'anyDate') {
                            setTimeout(() => {
                                const prevIndex = index - 1;
                                elements[prevIndex].focus();
                            }, 0);
                        } else {
                            event.preventDefault();
                            const prevIndex = index - 1;
                            if (elements[prevIndex].hasAttribute('disabled')) {
                                elements[prevIndex].removeAttribute('disabled');
                            }
                            elements[prevIndex].focus();
                        }
                    }
                }
            });
        });
    }
    publishedInFocusHandler() {
        const autocompleteWrapperClassList = this.elements.publishedInInput?.parentElement?.classList;
        const autocompleteTag = this.domUtils.getElementByClassName(this.selectors.autoCompleteTag , this.elements.publishedInInput?.parentElement)
        autocompleteWrapperClassList?.toggle("focused", !autocompleteWrapperClassList?.contains("focused"));
        if (autocompleteTag){
            this.elements.publishedInInput.removeAttribute("placeholder");
            this.elements.publishedInInput.style.width = "auto"
        }else{
            this.elements.publishedInInput?.setAttribute("placeholder" , 'Enter a journal, book, or reference work title')
        }

   }  
}
export default AdvancedSearch;